import { useState, useMemo, useRef, useEffect } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { Backdrop, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import EmailViewModal from "./modals/emailmodal";
import api from "../api/api";
import dayjs from "dayjs";
import ToastMessage from "../utils/mui/righttoptoast";
import { drag } from "./svg";

// Utility to render status badges
const statusChip = (rawStatus, { active = false } = {}) => {
  const code = (rawStatus || "").toUpperCase().trim();
  const map = {
    A: { label: "Active", cls: "text-[#22C55E] bg-[#F0FDF4] border border-[#22C55E]" },
    I: { label: "Inactive", cls: "text-[#4A5565] bg-[#F1F5F9] border border-[#99A1AF]" },
    P: { label: "Pending", cls: "text-[#E17954] bg-[#FAF5FF] border border-[#E17954]" },
  };
  const s = map[code] || { label: "—", cls: "text-[#667085] bg-[#E7E9F0]" };
  const activeCls = active ? "bg-[#FFFFFF1A] text-white border border-white/40" : "";
  return (
    <span className={`px-3 py-[3px] rounded-full text-xs font-medium ${s.cls} ${activeCls}`}>
      {s.label}
    </span>
  );
};

const EmailTable = ({ loading, emails, refresh }) => {
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
const [iscollapse,setIsCollapse]=useState(false)
  // Selection / detail
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [openView, setOpenView] = useState(false);

  // Delete confirmation
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
const [openMenuId, setOpenMenuId] = useState(null);

  // Toast
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);
  const handleToastClose = () => setOpenToast(false);

  const practiseid = window.getPracticeId();

  const totalPages = Math.ceil((emails?.length || 0) / itemsPerPage);
  const paginatedData = useMemo(
    () => emails?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) || [],
    [emails, currentPage, itemsPerPage]
  );

  const handlePrev = () => currentPage > 1 && setCurrentPage(p => p - 1);
  const handleNext = () => currentPage < totalPages && setCurrentPage(p => p + 1);
  const handlePerPageChange = e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); };

  const fetchEmailById = async id => {
    try {
      setIsLoading(true);
      const res = await api.get(`/v1/${practiseid}/email/get/${id}`);
      setSelectedEmailId(id);
      setSelectedEmail(res?.data || null);
    } catch (e) {
      console.error(e);
      setSelectedEmailId(id);
      setSelectedEmail(null);
    } finally {
      setIsLoading(false);
    }
  };
const menuRef = useRef(null);

useEffect(() => {
  function handleClickOutside(e) {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setOpenMenuId(null);   // close menu
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  const deletemail = async id => {
    try {
      setDeleting(true);
      const response = await api.delete(`/v1/${practiseid}/email/update-status/${id}?status=I`);
      if (response.status === 200) {
        setToastSeverity("success");
        setToastMessage("Email deleted successfully!");
      } else {
        setToastSeverity("info");
        setToastMessage(response?.message || "Something went wrong");
      }
      setOpenToast(true);
    } catch (error) {
      console.error(error);
      setToastSeverity("error");
      setToastMessage("Something went wrong");
      setOpenToast(true);
    } finally {
      setDeleting(false);
      setOpenDeleteConfirm(false);
      setEmailToDelete(null);
      refresh();
    }
  };

  const handleDeleteClick = id => {
    setEmailToDelete(id);
    setOpenDeleteConfirm(true);
  };

  return (
    <div className="w-full rounded-[20px] bg-[#FFF] p-2">
        <div className="w-full border-b border-[#E5E7EB] flex justify-between items-center mb-4 text-start p-2 px-3 pb-4 ">
                 
                    <div className="text-[14px] font-normal font-inter text-[#232323]">
                    Email
                    </div>
                <div
  className="cursor-pointer"
  onClick={() => setIsCollapse(!iscollapse)}
>
  {drag(iscollapse ? "green" : "#e17954")}
</div>

                </div>
      {/* Master–Detail Split */}
      <div className="w-full flex flex-col sm:flex-row gap-4 min-h-[60vh] max-h-[82vh]">
        {/* LEFT: list */}
        {iscollapse?<div>

  <div className="flex flex-col gap-2 mt-3">

  {paginatedData.map((email, idx) => {
    const isActive = selectedEmailId === email.id;
    const isMenuOpen = openMenuId === email.id;

    return (
      <div key={email.id} className="relative">
        
        <button
          onClick={() => fetchEmailById(email.id)}
          className={`
            relative w-full
            px-3 py-2 text-sm text-white
            ${isActive ? "bg-[#50869A]" : "bg-[#a4bcc3]"}
            rounded-[3.5px]
            shadow-[0_4px_6px_-1px_rgba(0,0,0,0.10),0_2px_4px_-2px_rgba(0,0,0,0.10)]
            transition active:scale-95
          `}
        >
          {idx + 1}
        </button>

        {/* 2 dots (menu trigger) */}
        <div
          className="absolute bottom-[5px] right-[3px] flex items-center gap-[2px] cursor-pointer"
          onClick={(e) => {
            e.stopPropagation(); // prevent selecting the email
            setOpenMenuId(isMenuOpen ? null : email.id);
          }}
        >
          <div className={`w-[3px] h-[3px] ${isActive ? "bg-white" : "bg-[#7E7E7E]"} rounded-full`}></div>
          <div className={`w-[3px] h-[3px] ${isActive ? "bg-white" : "bg-[#7E7E7E]"} rounded-full`}></div>
        </div>

        {/* Mini action menu */}
        {isMenuOpen && (
          <div
           ref={menuRef}
            className="absolute right-0 bottom-[26px] bg-white border shadow-md rounded-md text-xs z-50 min-w-[80px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={e => { e.stopPropagation(); setSelectedEmail(email); setOpenView(true); }}
            >
              View
            </div>
            <div
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-red-600"
              onClick={() => {
                handleDeleteClick(email.id);
                setOpenMenuId(null);
              }}
            >
              Delete
            </div>
          </div>
        )}

      </div>
    );
  })}

</div>







        </div>:
        <div className="w-full lg:w-[28%] flex flex-col">
          <div className="flex items-center justify-between px-2 py-2 text-sm text-[#667085]">
            <div>Total: {emails?.length || 0}</div>
            <div className="flex items-center gap-2">
              <span>Per Page:</span>
              <select
                value={itemsPerPage}
                onChange={handlePerPageChange}
                className="border rounded-md px-2 py-1 text-[#1d2951]"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pr-1">
            {loading ? (
              <div className="w-full py-10 text-center text-gray-500">Loading...</div>
            ) : paginatedData.length === 0 ? (
              <div className="w-full py-10 text-center text-gray-500">No Data Available</div>
            ) : (
              <ul className="space-y-3">
                {paginatedData.map((email, idx) => {
                  const isActive = selectedEmailId === email.id;
                  return (
                    <li
                      key={email.id}
                      className={`rounded-[10px] border border-[#E5E7EB] hover:bg-[#F8FAFC] transition-all cursor-pointer ${
                        isActive ? "!bg-[#50869A] text-white shadow-md" : "bg-white"
                      }`}
                      onClick={() => fetchEmailById(email.id)}
                    >
                      <div className="flex items-start gap-4 p-2">
                        <div className={`flex text-sm font-medium font-inter ${isActive ? "text-white" : "text-black"}`}>
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col items-start justify-start gap-1">
                            <div className={`truncate font-medium font-inter text-xs ${isActive ? "text-[#FFFFFF]" : "text-[#232323]"}`}>
                              {email.sender || "—"}
                            </div>
                            <div className={`truncate font-inter text-[10px] font-normal ${isActive ? "text-[#FFFFFFCC]" : "text-[#7E7E7E]"}`}>
                              {email.subject || "—"}
                            </div>
                            <div className={`text-[10px] font-inter font-normal ${isActive ? "text-[#FFFFFFCC]" : "text-[#7E7E7E]"}`}>
                              {dayjs(email.ts).format("MM-DD-YYYY h:mm A")}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {statusChip(email.status, { active: isActive })}
                          <RemoveRedEyeOutlinedIcon
                            fontSize="small"
                            className={`${isActive ? "text-white" : "text-[#6B7280]"}`}
                            onClick={e => { e.stopPropagation(); setSelectedEmail(email); setOpenView(true); }}
                          />
                          <DeleteOutlineIcon
                            fontSize="small"
                            className={`${isActive ? "text-white/80" : "text-[#9CA3AF]"}`}
                            onClick={e => { e.stopPropagation(); handleDeleteClick(email.id); }}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Pagination */}
          {emails?.length > 0 && (
            <div className="flex items-center justify-between mt-3 px-2 py-2">
              <button
                onClick={handlePrev}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-md text-white ${currentPage === 1 ? "bg-gray-400" : "bg-[#1d2951]"}`}
              >
                Prev
              </button>
              <span className="text-sm text-[#667085]">Page {currentPage} of {totalPages || 1}</span>
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-4 py-2 rounded-md text-white ${currentPage === totalPages || totalPages === 0 ? "bg-gray-400" : "bg-[#1d2951]"}`}
              >
                Next
              </button>
            </div>
          )}
        </div>}

        {/* RIGHT: detail view */}
        <div className="flex-1 min-w-[50%]  border border-[#E5E7EB] bg-white">
          <div className="flex flex-col gap-1 w-full border-b border-[#E5E7EB] p-2">
            <div className="text-sm flex justify-between items-center ">
              <div className="text-[#7E7E7E] text-[12px] font-normal font-inter">Sender:</div>
              <div className="font-medium font-inter text-[12px] text-[#232323] truncate">{selectedEmail?.sender || "—"}</div>
            </div>
            <div className="text-sm flex justify-between items-center ">
              <div className="text-[#7E7E7E] text-[12px] font-normal font-inter">Subject:</div>
              <div className="font-medium font-inter text-[12px] text-[#232323] truncate">{selectedEmail?.subject || "—"}</div>
            </div>
            <div className="text-sm flex justify-between items-center ">
              <div className="text-[#7E7E7E] text-[12px] font-normal font-inter">Body:</div>
            </div>
          </div>

          <div className="p-3">
            <div className="h-[62vh] bg-[#F3F4F6] rounded-xl p-3 overflow-auto">
              {selectedEmail ? (
                selectedEmail.body ? (
                  <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: selectedEmail.body }} />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-[#111827]">{selectedEmail.body || "No content."}</pre>
                )
              ) : (
                <div className="h-full w-full flex items-center justify-center text-[#98A2B3]">
                  Select an email from the list to preview.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this email? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button color="error" onClick={() => deletemail(emailToDelete)}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Loading Spinner */}
      <Backdrop sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }} open={isLoading || deleting}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Toast & Email Modal */}
      <ToastMessage message={toastMessage} severity={toastSeverity} open={openToast} handleClose={handleToastClose} />
      <EmailViewModal open={openView} handleClose={() => setOpenView(false)} emailData={selectedEmail} />
    </div>
  );
};

export default EmailTable;
